import React, { useState } from "react";
import "./App.css";
import logo from "./assets/images/objectplayer.png";
import founderImage from "./assets/images/Sham-Gir.png";

const services = [
  {
    number: "01",
    icon: "fa-bug-slash",
    title: "Security & Penetration Testing",
    description:
      "Manual and automated offensive testing across web applications, APIs, cloud environments, mobile products, and blockchain systems—designed to surface real exploit paths before attackers find them.",
    tags: ["Web & API pentesting", "Threat modeling", "Exploit validation"],
  },
  {
    number: "02",
    icon: "fa-file-shield",
    title: "Application & Smart Contract Audits",
    description:
      "In-depth security reviews of application codebases, architectures, and smart contracts with evidence-based findings, severity ratings, and actionable remediation guidance.",
    tags: ["Smart contract audits", "Application review", "Remediation support"],
  },
  {
    number: "03",
    icon: "fa-brain",
    title: "AI Security & Application Assessments",
    description:
      "Focused assessments for AI-powered applications and model integrations—covering prompt injection, data exposure, model misuse, and the security of AI-driven workflows.",
    tags: ["AI system review", "Prompt injection", "Model risk assessment"],
  },
  {
    number: "04",
    icon: "fa-compass-drafting",
    title: "Security Architecture & Advisory",
    description:
      "Senior technical guidance on secure system design, infrastructure decisions, compliance readiness, and embedding security into engineering processes from day one.",
    tags: ["Secure system design", "Security strategy", "Technical leadership"],
  },
  {
    number: "05",
    icon: "fa-code",
    title: "Web2 & Web3 Engineering",
    description:
      "End-to-end product engineering spanning traditional web platforms and decentralized systems—from APIs and cloud infrastructure to smart contracts and dApps.",
    tags: ["Full-stack development", "Smart contracts & DeFi", "dApp delivery"],
  },
  {
    number: "06",
    icon: "fa-robot",
    title: "AI Product Development & Integration",
    description:
      "Applied AI capabilities engineered into real products—intelligent automation, model-backed features, and data-driven workflows built with security and reliability in mind.",
    tags: ["Applied AI", "Workflow automation", "Model integration"],
  },
];

const process = [
  ["01", "Assess", "We establish business objectives, technical context, attack surface, constraints, and measurable outcomes."],
  ["02", "Architect", "We define the scope, system design, security controls, and delivery plan before implementation."],
  ["03", "Engineer & validate", "We execute in focused iterations, continuously reviewing quality, security, and operational readiness."],
  ["04", "Deliver & support", "You receive a production-ready outcome, clear technical documentation, and prioritized next steps."],
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#home" onClick={closeMenu} aria-label="Objectplayer Lab home">
          <img src={logo} alt="" />
          <span>Objectplayer <strong>Lab</strong></span>
        </a>
        <button
          className="menu-button"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span><span></span>
        </button>
        <nav className={menuOpen ? "nav-links open" : "nav-links"} aria-label="Main navigation">
          <a href="#services" onClick={closeMenu}>Services</a>
          <a href="#approach" onClick={closeMenu}>Approach</a>
          <a href="#founder" onClick={closeMenu}>Founder</a>
          <a className="nav-cta" href="#contact" onClick={closeMenu}>Start a project <span>↗</span></a>
        </nav>
      </header>

      <main>
        <section className="hero" id="home">
          <div className="hero-glow"></div>
          <div className="hero-copy">
            <p className="eyebrow"><span></span> Security-led engineering for Web2, Web3, and AI</p>
            <h1>Secure systems.<br />Intelligent <em>products.</em></h1>
            <p className="hero-lede">
              Objectplayer Lab helps organizations identify security risks, strengthen digital systems, and build dependable software. We combine offensive security testing, application and smart contract reviews, and full-stack engineering to help teams ship products with confidence.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#contact">Discuss your project <span>↗</span></a>
              <a className="text-link" href="#services">Explore our services <span>↓</span></a>
            </div>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="orbit orbit-one"><span></span></div>
            <div className="orbit orbit-two"><span></span></div>
            <div className="core-mark">
              <img src={logo} alt="" />
            </div>
            <div className="signal-card signal-one"><i className="fas fa-lock"></i> Security by design</div>
            <div className="signal-card signal-two"><i className="fas fa-layer-group"></i> Web2 · Web3 · AI</div>
          </div>
          <div className="trust-strip">
            <p>Capabilities across</p>
            <div><span>Cybersecurity</span><span>Web platforms</span><span>APIs & cloud</span><span>Blockchain</span><span>Applied AI</span><span>Mobile</span></div>
          </div>
        </section>

        <section className="proof-bar" aria-label="Objectplayer Lab credentials">
          <article><strong>10<span>+</span></strong><p>Systems security-tested</p></article>
          <article><strong>7<span>+</span></strong><p>Years of engineering experience</p></article>
          <article><strong className="proof-word">Full-cycle</strong><p>From assessment to production</p></article>
          <article><i className="fas fa-layer-group"></i><p><b>Cross-domain expertise</b>Web2 · Web3 · Applied AI</p></article>
        </section>

        <section className="section services" id="services">
          <div className="section-heading">
            <div>
              <p className="eyebrow"><span></span> What we do</p>
              <h2>From risk assessment<br />to <em>production.</em></h2>
            </div>
          </div>
          <div className="service-grid">
            {services.map(service => (
              <article className="service-card" key={service.title}>
                <div className="card-top"><span>{service.number}</span><i className={`fas ${service.icon}`}></i></div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul>{service.tags.map(tag => <li key={tag}>{tag}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section className="section approach" id="approach">
          <div className="approach-intro">
            <p className="eyebrow light"><span></span> How we work</p>
            <h2>Direct expertise.<br />Rigorous delivery.<br /><em>Clear outcomes.</em></h2>
            <p>Every engagement receives senior technical ownership, transparent communication, and security integrated from initial assessment through delivery.</p>
          </div>
          <div className="process-list">
            {process.map(item => (
              <article key={item[0]}>
                <span>{item[0]}</span>
                <div><h3>{item[1]}</h3><p>{item[2]}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section className="section founder" id="founder">
          <div className="founder-portrait">
            <div className="portrait-grid"></div>
            <div className="portrait-orbit"></div>
            <div className="portrait-frame">
              <img className="founder-image" src={founderImage} alt="Sham Gir, Founder of Objectplayer Lab" />
            </div>
            <div className="founder-badge"><span>7+ years</span><small>Engineering secure products</small></div>
          </div>
          <div className="founder-copy">
            <p className="eyebrow"><span></span> Meet the founder</p>
            <h2>Engineering depth with a security-first <em>perspective.</em></h2>
            <p className="founder-name">Sham Gir <span>Founder</span></p>
            <p>With more than seven years of engineering experience, Sham has designed, secured, and delivered digital products for startups and established organizations alike—spanning web platforms, APIs, mobile applications, smart contracts, and decentralized infrastructure.</p>
            <p>His approach combines deep technical knowledge of Web2, Web3, and applied AI with an offensive security mindset, allowing Objectplayer Lab to deliver products that are not only well-built, but resilient by design.</p>
            <div className="founder-links">
              <a href="https://github.com/ObjectPlayer" target="_blank" rel="noopener noreferrer"><i className="fab fa-github"></i> GitHub</a>
              <a href="https://www.linkedin.com/in/sham-gir-objectplayer-513a99132/" target="_blank" rel="noopener noreferrer"><i className="fab fa-linkedin-in"></i> LinkedIn</a>
              <a href="https://medium.com/@objectplayer" target="_blank" rel="noopener noreferrer"><i className="fab fa-medium-m"></i> Medium</a>
            </div>
          </div>
        </section>

        <section className="section contact" id="contact">
          <div>
            <p className="eyebrow light"><span></span> Start a conversation</p>
            <h2>Have a system to secure<br />or an idea to <em>build?</em></h2>
          </div>
          <div className="contact-side">
            <p>Tell us what you are working on, where you are in the process, and what success looks like.</p>
            <a className="button inverse" href="mailto:hello@objectplayer.com">hello@objectplayer.com <span>↗</span></a>
          </div>
        </section>
      </main>

      <footer>
        <a className="brand footer-brand" href="#home"><img src={logo} alt="" /><span>Objectplayer <strong>Lab</strong></span></a>
        <p>Security and product engineering across Web2, Web3, and applied AI.</p>
        <div><span>© {new Date().getFullYear()} Objectplayer Lab</span><a href="#home">Back to top ↑</a></div>
      </footer>
    </div>
  );
}

export default App;
